import {Dropdown as PepsicoDsDropdown} from 'pepsico-ds'
/**
 * Dropdown component that renders a dropdown menu with various customization options.
 *
 * @param {Object} props - The properties object.
 * @param {any} props.value - The current value of the dropdown.
 * @param {Function} props.updateValue - Function to update the value of the dropdown.
 * @param {string} [props.label=''] - The label for the dropdown.
 * @param {Object} props.schema - The schema object containing options and other configurations.
 * @param {Object} props.uiSchema - The UI schema object containing UI-specific configurations.
 * @param {boolean} [props.required=false] - Indicates if the dropdown is required.
 * @param {boolean} [props.isError=false] - Indicates if there is an error.
 * @param {boolean} [props.isJsonWrap=true] - Determines if the dropdown should use JSON wrapping.
 * @param {Array} [props.childList=[]] - List of child elements for the dropdown.
 * @param {string} [props.dropdownHeader=''] - The header for the dropdown.
 * @param {string} [props.className=''] - Additional class names for the dropdown.
 * @param {boolean} [props.isDark=true] - Indicates if the dropdown should use a dark theme.
 * @param {('single'|'multiple')} [props.selection='single'] - The selection mode for the dropdown.
 * @param {('small'|'medium')} [props.size='medium'] - The size of the dropdown.
 * @param {string} [props.placeholder=''] - The placeholder text for the dropdown.
 * @param {string} [props.helperText=''] - The helper text for the dropdown.
 * @param {string} [props.errorMessage=''] - The error message for the dropdown.
 * @param {boolean} [props.isClearable=true] - Indicates if the dropdown can be cleared.
 * @returns {JSX.Element} The rendered Dropdown component.
 */
export default function Dropdown({
  value,
  updateValue,
  label = '',
  schema,
  uiSchema,
  required = false,
  isError = false,
  isJsonWrap = true,
  childList = [],
  dropdownHeader = '',
  className = '',
  isDark = true,
  selection = 'single',
  size = 'medium',
  placeholder = '',
  helperText = '',
  errorMessage = '',
  isClearable = true,
}) {
  const newValue = selectedValue => {
      updateValue(selectedValue)
  }

  const dropdownId = `dropdown-${label.replace(/\s+/g, '-').toLowerCase()}`
 
  const getIsError = () => uiSchema.value === '' || isError;

  const getHelperText = () => {
    const isError = getIsError()
    if (isError) {
      return uiSchema?.errorMessage
    }
    return helperText
  }

  return (
    <>
      {isJsonWrap ? (
        <PepsicoDsDropdown
          id={dropdownId}
          isRequired={required}
          childList={schema.options}
          dropdownHeader={schema.header}
          style={{minWidth: '0px'}}
          className={uiSchema?.className || ''}
          isDark={uiSchema?.isDark || ''}
          label={label}
          error={getIsError()}
          helperText={getHelperText()}
          selection={uiSchema.selection || 'single'}
          selectedValue={value || ''} // in case of undefined we should pass empty string to clear input field value
          setSelectedValue={newValue}
          size={uiSchema.size || 'medium'}
          placeholder={uiSchema.placeholder || ''}
          isClearable={isClearable}
        />
      ) : (
        <PepsicoDsDropdown
          id={dropdownId}
          childList={childList}
          dropdownHeader={dropdownHeader}
          style={{minWidth: '0px'}}
          className={className || ''}
          isDark={isDark || ''}
          label={label}
          error={isError}
          selection={selection}
          selectedValue={value || ''} // in case of undefined we should pass empty string to clear input field value
          setSelectedValue={newValue}
          size={size}
          placeholder={placeholder}
          isRequired={required}
          helperText={isError ? errorMessage : helperText}
          isClearable={isClearable}
        />
      )}
    </>
  )
}
